import { useEffect, useState, useMemo } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, RefreshCw, Zap, Sunrise, Calendar, FileText, BellRing } from "lucide-react";
import PhoneFrame from "@/components/PhoneFrame";
import SectionLabel from "@/components/SectionLabel";

const HERO_CYCLE_MS = 11000;

const ICONS = {
  sunrise: Sunrise,
  calendar: Calendar,
  file: FileText,
  bell: BellRing,
};

export default function Hero() {
  const { t, locale } = useLanguage();
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCycle((c) => c + 1), HERO_CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  const phoneT = t("hero.phone");
  const pills = t("hero.pills");

  const script = useMemo(
    () => ({
      header: { title: phoneT.header_title, status: phoneT.header_status },
      user: phoneT.user_msg,
      ai: phoneT.ai_msg,
      statuses: phoneT.statuses,
      result: { title: phoneT.result_title, body: phoneT.result_body, meta: phoneT.result_meta },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale]
  );

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden paper-grain"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden>
        <div className="aurora-blob b1 aurora-drift-1" style={{ top: "12%", left: "22%", width: 380, height: 380, opacity: 0.24 }} />
        <div className="aurora-blob b2 aurora-drift-2" style={{ top: "30%", right: "16%", width: 420, height: 420, opacity: 0.2 }} />
      </div>

      <div className="container-iko relative pt-12 pb-14 lg:pt-20 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* LEFT — text */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <div className="fade-up inline-flex" data-testid="hero-badge">
              <SectionLabel icon={Zap}>{t("hero.badge")}</SectionLabel>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display font-semibold text-[#1A1814] leading-[1.0] tracking-[-0.034em] text-balance fade-up"
              style={{ fontSize: "clamp(2.6rem, 6vw, 5.5rem)", animationDelay: "60ms" }}
            >
              <span className="block">{t("hero.title_l1")}</span>
              <span className="block">{t("hero.title_l2")}</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-lg sm:text-xl max-w-[600px] mx-auto lg:mx-0 text-[#6B6862] leading-relaxed fade-up"
              style={{ animationDelay: "140ms" }}
            >
              {t("hero.subtitle")}
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center lg:justify-start fade-up"
              style={{ animationDelay: "200ms" }}
            >
              <a
                href="#waitlist"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 pl-6 pr-5 py-3.5 rounded-full bg-[#1A1814] text-white text-[14px] font-medium hover:bg-[#437DEA] hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(67,125,234,0.55)] transition-all duration-300"
              >
                {t("hero.cta_primary")}
                <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href="#how"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white/70 border border-[#ECE8DD] text-[#1A1814] text-[14px] font-medium hover:bg-white hover:-translate-y-0.5 hover:border-[#437DEA]/30 transition-all duration-300 backdrop-blur"
              >
                {t("hero.cta_secondary")}
                <ArrowRight size={14} strokeWidth={2} className="opacity-50 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>

            {/* ROI PILLS — compact 2x2, max 660px, big number + short context */}
            <div
              className="mt-9 grid grid-cols-2 gap-3 max-w-[660px] mx-auto lg:mx-0 fade-up"
              style={{ animationDelay: "300ms" }}
              data-testid="hero-pills"
            >
              {pills.map((p, i) => {
                const Icon = ICONS[p.icon] || Sunrise;
                return (
                  <div
                    key={i}
                    data-testid={`hero-pill-${i}`}
                    className="relative flex items-center gap-3.5 px-4 py-3 rounded-2xl bg-white/85 border border-[#E8E2D8] backdrop-blur hover:border-[#437DEA]/35 hover:bg-white hover:-translate-y-0.5 transition-all duration-300 shadow-[0_4px_12px_-6px_rgba(26,24,20,0.06)] min-h-[78px]"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-[#F4F2EC] border border-[#F0EBE0] flex items-center justify-center text-[#437DEA]">
                      <Icon size={15} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-[22px] lg:text-[26px] text-[#1A1814] tracking-[-0.024em] leading-none mb-1">
                        {p.value}
                      </div>
                      <div className="text-[12px] text-[#6B6862] leading-snug">
                        {p.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p
              className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#94918A] max-w-2xl mx-auto lg:mx-0 fade-up"
              style={{ animationDelay: "360ms" }}
            >
              {t("hero.pills_note")}
            </p>
          </div>

          {/* RIGHT — phone only, no floating bubbles */}
          <div className="lg:col-span-5 flex justify-center fade-up" style={{ animationDelay: "260ms" }}>
            <div className="relative w-full max-w-[340px]">
              <div className="float-y">
                <PhoneFrame
                  key={`hero-${locale}-${cycle}`}
                  script={script}
                  testIdPrefix="hero-phone"
                />
              </div>
              <button
                onClick={() => setCycle((c) => c + 1)}
                data-testid="hero-phone-replay"
                className="mx-auto mt-5 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 border border-[#ECE8DD] text-[11px] font-medium text-[#6B6862] hover:text-[#1A1814] hover:bg-white transition-colors backdrop-blur"
              >
                <RefreshCw size={11} /> {phoneT.replay}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
